import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { MediaType, AppView, LibraryCollection } from './types';
import { COLLECTIONS_CONFIG } from './constants/collections';

import styles from './App.module.css';

// --- CONTEXTS AND HOOKS ---
import { useConfig } from './context/ConfigContext';
import { useSupabase } from './context/SupabaseContext';
import { useLibrary } from './hooks/useLibrary';

// --- COMPONENTS ---
import { ProfileSwitcher } from './components/ProfileSwitcher';
import { DetailsModal } from './components/DetailsModal';
import { ScrollToTop } from './components/ScrollToTop';

// --- VIEWS ---
import { DiscoveryView } from './views/DiscoveryView';
import { SearchView } from './views/SearchView';
import { LibraryView } from './views/LibraryView';

// --- CONSTANTS ---
const DEFAULT_TAB: MediaType = 'movie';
const DEFAULT_VIEW_MODE: AppView = 'discovery';
const DEFAULT_RATING: number = 0;

export default function App() {
  const { tmdbApiKey } = useConfig();
  const { activeProfile, selectProfile } = useSupabase();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- REFS ---
  const cache = React.useRef<Record<string, any>>({});

  // --- URL PARAMS ---
  const AppView = (searchParams.get('mode') || DEFAULT_VIEW_MODE) as AppView;
  const activeTab = (searchParams.get('type') || DEFAULT_TAB) as MediaType;

  const getP = (key: string, def: string) => {
    const prefix = AppView === 'discovery' ? `${AppView}_${activeTab}` : AppView;
    return searchParams.get(`${prefix}_${key}`) || def;
  };

  const currentPage = Number(getP('page', '1'));
  const sortBy = getP('sort', 'popularity');
  const isDescending = getP('dir', 'desc') === 'desc';
  const minRating = Number(getP('minR', DEFAULT_RATING.toString()));
  const hiddenCollections = Object.entries(COLLECTIONS_CONFIG).reduce((acc, [key, config]) => {
    const collection = key as LibraryCollection;
    acc[collection] = getP(config.urlKey, 'true') === 'true';
    return acc;
  }, {} as Record<LibraryCollection, boolean>);
  const startDate = getP('sDate', '1900-01-01');
  const endDate = getP('eDate', new Date().toISOString().split('T')[0]);
  const activeGenres = getP('genres', '');
  const searchQuery = getP('q', '');

  const modalId = searchParams.get('id');
  const modalType = searchParams.get('mType') as MediaType | null;

  // --- LIBRARY STATE ---
  const { library, updateCollection } = useLibrary(activeProfile?.id);

  // --- LOGIC FUNCTIONS ---

  // Update the url params
  const updateParams = (newParams: Record<string, string | null>, isSwitchingViews = false) => {
    const targetMode = (newParams.mode || AppView) as AppView;
    const targetType = (newParams.type || activeTab) as MediaType;
    const targetPrefix = targetMode === 'discovery' ? `${targetMode}_${targetType}` : targetMode;

    const globalKeys = ['mode', 'type', 'id', 'mType'];
    const nextParams: Record<string, string> = {};

    // Maintain Global State
    globalKeys.forEach(key => {
      const val = newParams[key] !== undefined ? newParams[key] : searchParams.get(key);
      if (val) nextParams[key] = val;
    });

    // Identify and Save View-Specific Params
    const viewData: Record<string, string> = {};

    // If we aren't switching views, we merge current URL params into our cache
    if (isSwitchingViews) {
      Object.entries(newParams).forEach(([key, value]) => {
        if (!globalKeys.includes(key) && typeof value === 'string') {
          viewData[key] = value;
        }
      });
    } else {
      // Sync current URL params into our local cache
      searchParams.forEach((value, key) => {
        if (key.startsWith(targetPrefix + '_')) {
          const cleanKey = key.replace(targetPrefix + '_', '');
          viewData[cleanKey] = value;
        }
      });
    }
    // Merge in the explicit new updates
    Object.entries(newParams).forEach(([key, value]) => {
      if (!globalKeys.includes(key)) {
        if (value === null) {
          delete viewData[key];
        } else {
          viewData[key] = value;
        }
      }
    });

    // Save to memory
    cache.current[targetPrefix] = viewData;
    localStorage.setItem(`nav_cache_${targetPrefix}`, JSON.stringify(viewData));

    // Build the clean URL
    Object.entries(viewData).forEach(([key, value]) => {
      nextParams[`${targetPrefix}_${key}`] = value;
    });

    setSearchParams(nextParams);
  };

  const handleTabChange = (type: MediaType) => {
    const nextPrefix = AppView === 'discovery' ? `discovery_${type}` : AppView;
    const savedParams = cache.current[nextPrefix] || { page: '1' };

    updateParams({ ...savedParams, type, mode: AppView }, true);
  }

  const handleModeChange = (mode: AppView) => {
    const nextPrefix = mode === 'discovery' ? `discovery_${activeTab}` : mode;
    const savedParams = cache.current[nextPrefix] || {};

    updateParams({ ...savedParams, mode }, true);
  }

  const openModal = (id: number, type: 'movie' | 'tv') => {
    updateParams({ id: id.toString(), mType: type });
  }

  const closeModal = () => {
    updateParams({ id: null, mType: null });
  }

  // --- EFFECTS ---
  useEffect(() => {
    const keys = ['discovery_movie', 'discovery_tv', 'search', 'library'];
    keys.forEach(key => {
      const saved = localStorage.getItem(`nav_cache_${key}`);
      if (saved) cache.current[key] = JSON.parse(saved);
    });
  }, []);

  // Ensure scroll gets re enabled after details modal close
  useEffect(() => {
    if (!modalId) document.body.style.overflow = 'unset';
  }, [modalId]);

  // Scroll to results on page change
  useEffect(() => {
    // Only trigger if we are navigating deep into pages
    if (currentPage > 1) {
      const timer = setTimeout(() => {
        const element = document.getElementById('results-top');
        if (element) {
          // 2. Use 'block: "start"' to ensure it aligns with the top
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // --- RENDER ---
  if (!activeProfile) return <ProfileSwitcher />;

  const currentCollection = library.find(
    m => m.id === Number(modalId) && m.media_type === modalType
  )?.collection;

  return (
    <div className={styles.pageContainer}>
      <header className={styles.header}>
        <div className={styles.headerTopBar}>
          <div className={styles.typeToggleContainer}>
            <button className={`${styles.toggleBtn} ${activeTab === 'movie' ? styles.activeTab : ''}`} onClick={() => handleTabChange('movie')}>Films</button>
            <button className={`${styles.toggleBtn} ${activeTab === 'tv' ? styles.activeTab : ''}`} onClick={() => handleTabChange('tv')}>Séries</button>
          </div>

          <div className={styles.modeToggleContainer}>
            <button className={`${styles.modeBtn} ${AppView === 'discovery' ? styles.activeMode : ''}`} onClick={() => handleModeChange('discovery')}>Découverte</button>
            <button className={`${styles.modeBtn} ${AppView === 'search' ? styles.activeMode : ''}`} onClick={() => handleModeChange('search')}>Recherche</button>
            <button className={`${styles.modeBtn} ${AppView === 'library' ? styles.activeMode : ''}`} onClick={() => handleModeChange('library')}>Bibliothèque</button>
          </div>

          <div className={styles.profileBtn} onClick={() => selectProfile(null)}>
            <img src={"/assets/images/avatars/" + activeProfile?.avatar_url} alt="Profile" className={styles.headerAvatar} />
          </div>
        </div>
      </header>

      {/* --- MAIN CONTENT ROUTING --- */}
      <main className={styles.mainContent}>

        {AppView === 'discovery' && (
          <DiscoveryView
            apiKey={tmdbApiKey}
            activeTab={activeTab}
            currentPage={currentPage}
            sortBy={sortBy}
            isDescending={isDescending}
            minRating={minRating}
            hiddenCollections={hiddenCollections}
            startDate={startDate}
            endDate={endDate}
            activeGenres={activeGenres}
            updateParams={updateParams}
            library={library}
            updateCollection={updateCollection}
            onOpenModal={openModal}
          />
        )}

        {AppView === 'search' && (
          <SearchView
            apiKey={tmdbApiKey}
            activeTab={activeTab}
            searchQuery={searchQuery}
            currentPage={currentPage}
            updateParams={updateParams}
            library={library}
            updateCollection={updateCollection}
            onOpenModal={openModal}
          />
        )}

        {AppView === 'library' && (
          <LibraryView
            activeTab={activeTab}
            library={library}
            updateCollection={updateCollection}
            onOpenModal={openModal}
          />
        )}
      </main>

      {/* --- MODAL --- */}
      {modalId && modalType && (
        <DetailsModal
          itemId={Number(modalId)}
          mediaType={modalType}
          onClose={closeModal}
          currentCollection={currentCollection}
          updateCollection={updateCollection}
        />
      )}
      
      <ScrollToTop />
    </div>
  );
}